import { fetchWithAuth } from '../utils/fetchWithAuth';
import { unwrapResponse as unwrap } from '../utils/unwrapResponse';

export type DecisionRecipient = 'assignee' | 'reporter' | 'group_members';
export type DecisionKind = 'approve' | 'reject';

export interface ValidationGroup {
  id: number;
  service_desk_id: number;
  status_id: number;
  name: string;
  is_active: boolean;
  // Jira transition ids offered at this gate; empty = all transitions.
  allowed_transitions: string[];
  // Who receives the decision email when a ticket leaves this gate.
  decision_recipients: DecisionRecipient[];
  // Entered-status id → approve/reject, from the gate's qualified transitions.
  decision_kinds: Record<string, DecisionKind>;
  member_count: number;
  // Member usernames, so the listing's CSV export names them instead of counting them.
  member_names: string[];
  // Label of the gate status; null when Jira couldn't be reached.
  status_name: string | null;
}

export interface GroupMember {
  user_id: number;
  username: string;
  email: string;
  lang: string;
}

export interface DeskStatus {
  id: string;
  name: string;
  // Issue types this status belongs to, to scope the list to a request type.
  issueTypeIds: string[];
}

export interface DeskRequestType {
  id: string;
  name: string;
  issueTypeId: string;
}

export interface GateTransition {
  id: string;
  name: string;
  // Target status of the transition, when the workflow API exposes it.
  toStatusId?: string;
}

export interface GpsUser {
  id: number;
  username: string;
  email: string;
  // service_desk_id is JSONB: a number, an array of numbers, or "*".
  serviceDeskId: number | string | (number | string)[];
}

export interface CreateGroupInput {
  serviceDeskId: number;
  statusId: number;
  name: string;
  allowedTransitions?: string[];
  decisionRecipients?: DecisionRecipient[];
  decisionKinds?: Record<string, DecisionKind>;
}

const jsonHeaders = { 'Content-Type': 'application/json' };

export const fetchGroups = async (serviceDeskId?: number): Promise<ValidationGroup[]> => {
  const qs = serviceDeskId != null ? `?serviceDeskId=${serviceDeskId}` : '';
  const res = await fetchWithAuth(`admin/groups${qs}`);
  return unwrap<ValidationGroup[]>(res, 'Failed to fetch groups');
};

export const createGroup = async (input: CreateGroupInput): Promise<ValidationGroup> => {
  const res = await fetchWithAuth('admin/groups', {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify(input),
  });
  return unwrap<ValidationGroup>(res, 'Failed to create group');
};

export type UpdateGroupInput = Partial<CreateGroupInput & { isActive: boolean }>;

export const updateGroup = async (id: number, data: UpdateGroupInput): Promise<ValidationGroup> => {
  const res = await fetchWithAuth(`admin/groups/${id}`, {
    method: 'PUT',
    headers: jsonHeaders,
    body: JSON.stringify(data),
  });
  return unwrap<ValidationGroup>(res, 'Failed to update group');
};

export const deleteGroup = async (id: number): Promise<void> => {
  const res = await fetchWithAuth(`admin/groups/${id}`, { method: 'DELETE' });
  return unwrap<void>(res, 'Failed to delete group');
};

export const fetchGroupMembers = async (id: number): Promise<GroupMember[]> => {
  const res = await fetchWithAuth(`admin/groups/${id}/members`);
  return unwrap<GroupMember[]>(res, 'Failed to fetch group members');
};

export const addGroupMember = async (id: number, userId: number): Promise<void> => {
  const res = await fetchWithAuth(`admin/groups/${id}/members`, {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ userId }),
  });
  return unwrap<void>(res, 'Failed to add member');
};

export const removeGroupMember = async (id: number, userId: number): Promise<void> => {
  const res = await fetchWithAuth(`admin/groups/${id}/members/${userId}`, { method: 'DELETE' });
  return unwrap<void>(res, 'Failed to remove member');
};

export const fetchDeskStatuses = async (serviceDeskId: number): Promise<DeskStatus[]> => {
  const res = await fetchWithAuth(`admin/groups/statuses?serviceDeskId=${serviceDeskId}`);
  return unwrap<DeskStatus[]>(res, 'Failed to fetch statuses');
};

export const fetchDeskRequestTypes = async (serviceDeskId: number): Promise<DeskRequestType[]> => {
  const res = await fetchWithAuth(`admin/groups/request-types?serviceDeskId=${serviceDeskId}`);
  return unwrap<DeskRequestType[]>(res, 'Failed to fetch request types');
};

export const fetchGateTransitions = async (
  serviceDeskId: number,
  statusId: number
): Promise<GateTransition[]> => {
  const res = await fetchWithAuth(`admin/groups/transitions?serviceDeskId=${serviceDeskId}&statusId=${statusId}`);
  return unwrap<GateTransition[]>(res, 'Failed to fetch gate transitions');
};

export const fetchGpsUsers = async (): Promise<GpsUser[]> => {
  const res = await fetchWithAuth('users');
  return unwrap<GpsUser[]>(res, 'Failed to fetch users');
};
